import * as sdpTransform from 'sdp-transform';

import type {PexipMediaLine} from './types';

/** One media section of a {@link SessionDescriptionSummary}. */
export interface MediaSectionSummary {
    mid?: string;
    /** The media type, such as `audio`, or `unknown` when it does not parse. */
    kind: string;
    direction?: RTCRtpTransceiverDirection;
    /** Pexip's `a=content` attribute, such as `main` or `slides`. */
    content?: string;
    /** Distinct codec names in payload order. */
    codecs: string[];
}

/**
 * The negotiation structure of a session description, for routine logs. It
 * holds no SDP text, so no credentials, addresses, fingerprints, or stream
 * identifiers.
 */
export interface SessionDescriptionSummary {
    type?: RTCSdpType;
    media: MediaSectionSummary[];
}

const summarizeMedia = (media: PexipMediaLine): MediaSectionSummary => {
    // `sdp-transform` parses a numeric `a=mid` as a number and leaves `type`
    // unset on a malformed `m=` line, despite its declared types.
    const mid: unknown = media.mid;
    const kind: unknown = media.type;
    return {
        ...(mid === undefined ? {} : {mid: String(mid)}),
        kind: typeof kind === 'string' ? kind : 'unknown',
        ...(media.direction ? {direction: media.direction} : {}),
        ...(media.content ? {content: media.content} : {}),
        codecs: [...new Set(media.rtp.map(({codec}) => codec))],
    };
};

/**
 * Summarizes a session description for routine logs. Log the description
 * itself only at a detailed level, where log redaction masks its credentials.
 *
 * @example
 * summarizeSessionDescription({type: 'offer', sdp});
 * // {type: 'offer', media: [{mid: '0', kind: 'audio', direction: 'sendrecv', codecs: ['opus']}]}
 */
export const summarizeSessionDescription = (
    description: RTCSessionDescriptionInit | undefined,
): SessionDescriptionSummary => ({
    ...(description?.type ? {type: description.type} : {}),
    media:
        typeof description?.sdp === 'string'
            ? sdpTransform.parse(description.sdp).media.map(summarizeMedia)
            : [],
});
